import styled from '@emotion/styled'
import { ApiFullLandingPageLink, ApiReview } from '@storefronts/common-types'
import React, {
    MouseEventHandler,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { getReviews } from '../../../../api'
import { CustomerPhotos } from './CustomerPhotos'
import {
    DEFAULT_LOCALE,
    INITIAL_VISIBLE_REVIEW_COUNT,
    MIN_VISIBLE_REVIEW_COUNT,
} from '../../../constants'
import { AnalyticsContext } from '../../../context/analyticsContext'
import { ContentBlockContext } from '../../../context/contentBlockContext'
import {
    PRIMARY_PAGE_TEXT_COLOR_VAR,
    PRIMARY_PAGE_FILL_COLOR_VAR,
    SECONDARY_FONT_CSS_VAR,
} from '../../../context/CustomizationContextProvider'
import { ProductContext } from '../../../context/productContext'
import { ViddyLPContext } from '../../../context/viddyLPContext'
import {
    inScrollingLayout,
    scaledFontSize,
    ScrollingLayout,
} from '../../../style/scrollingLayout'
import { Constants } from '../../../style/styleConstants'
import { getTypographyRoleStyle } from '../../../style/typography'
import { TYPOGRAPHY_SIZE } from '../../../style/typographySizes'
import { getReviewSummary } from '../../../utils/getReviewSummary'
import { getDisplayData } from '../../../utils/productDisplay'
import { AiCustomerReviewSummary } from './AiCustomerReviewSummary'
import { ReviewCard } from './ReviewCard'
import { ReviewStar } from './ReviewStar'

const SeeMoreReviewsButton: React.FC<{ onClick: MouseEventHandler }> = ({
    onClick,
}) => {
    return (
        <div
            onClick={onClick}
            style={{
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '8px 16px',
                borderRadius: 999,
                border: `1px solid var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`,
                backgroundColor: 'transparent',
                color: `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`,
            }}
        >
            <span
                style={{
                    ...getTypographyRoleStyle('buttonText', {
                        fontWeight: 500,
                    }),
                    fontSize: scaledFontSize(13),
                }}
            >
                See More Reviews
            </span>
        </div>
    )
}

export const ReviewsSummary: React.FC<{
    lpLink: ApiFullLandingPageLink
    showHeading?: boolean
    interactive?: boolean
}> = ({ lpLink, showHeading = false, interactive = true }) => {
    const { landingPage } = useContext(ViddyLPContext)
    const { reviewsRef } = useContext(ProductContext)
    const { contentReviewsRef } = useContext(ContentBlockContext)
    const { Analytics } = useContext(AnalyticsContext)

    if (!lpLink?.reviews) return null // fixes lpLink undefined error on /landing/c361d243-ff43-4e80-9fca-9a80cf52a481

    const { reviewRating, reviewCount } = getReviewSummary(landingPage, lpLink)

    const showReviewCountText =
        reviewCount != null && reviewCount > MIN_VISIBLE_REVIEW_COUNT

    const onClick = interactive
        ? () => {
              // eslint-disable-next-line @typescript-eslint/no-unused-expressions
              reviewsRef &&
                  reviewsRef.current?.scrollIntoView({
                      behavior: 'smooth',
                      block: 'start',
                  })
              // eslint-disable-next-line @typescript-eslint/no-unused-expressions
              contentReviewsRef &&
                  contentReviewsRef.current?.scrollIntoView({
                      behavior: 'smooth',
                      block: 'start',
                  })
              if (contentReviewsRef) {
                  Analytics.TRACK_REVIEWS_SUMMARY_CLICK('content-block')
              }
          }
        : undefined

    return (
        <div
            onClick={onClick}
            style={{
                marginBottom: Constants.SECTION_BASE_SPACING_PX,
                cursor: interactive ? 'pointer' : undefined,
            }}
        >
            {showHeading && (
                <h2
                    style={{
                        ...getTypographyRoleStyle('headingText', {
                            fontFamily: `var(${SECONDARY_FONT_CSS_VAR})`,
                            fontWeight: 700,
                        }),
                        fontSize: scaledFontSize(TYPOGRAPHY_SIZE.SECTION),
                        color: `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`,
                        margin: `0 0 ${Constants.SECTION_BASE_SPACING_PX}px`,
                    }}
                >
                    Customer reviews
                </h2>
            )}
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                }}
            >
                {reviewRating != null && (
                    <>
                        <ReviewStar rating={reviewRating} starWidth={18} />
                        <span
                            style={{
                                color: `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`,
                                ...getTypographyRoleStyle('bodyText', {
                                    fontWeight: 400,
                                    lineHeight: 1.35,
                                }),
                                fontSize: scaledFontSize(TYPOGRAPHY_SIZE.BODY),
                            }}
                        >
                            {reviewRating.toFixed(1)} out of 5
                        </span>
                    </>
                )}
            </div>
            {showReviewCountText && (
                <div style={{ marginTop: 2 }}>
                    <span
                        style={{
                            ...getTypographyRoleStyle('captionText', {
                                fontWeight: 400,
                            }),
                            fontSize: scaledFontSize(14),
                            color: `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`,
                        }}
                    >
                        {reviewCount.toLocaleString(DEFAULT_LOCALE)} reviews
                    </span>
                </div>
            )}
        </div>
    )
}

const formatReviewCount = (count: number, showParentheses: boolean = false) => {
    const reviewText = `${count.toLocaleString(DEFAULT_LOCALE)} ${count === 1 ? 'review' : 'reviews'}`
    return showParentheses ? `(${reviewText})` : reviewText
}

export const ReviewsStars: React.FC<{
    count: number
    rating: number
    starSize: number
    fontSize: number
    starColor?: string
    textColor?: string
    showRating?: boolean
}> = ({
    count,
    starSize,
    fontSize,
    textColor = `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`,
    starColor,
    rating,
    showRating = true,
}) => {
    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 8,
            }}
        >
            <ReviewStar
                rating={rating}
                fillColor={starColor}
                starWidth={starSize || 15}
            />
            {showRating && (
                <span
                    style={{
                        color: textColor,
                        textAlign: 'center',
                        ...getTypographyRoleStyle('captionText', {
                            fontWeight: 400,
                        }),
                        fontSize,
                    }}
                >
                    {rating.toFixed(1)}
                </span>
            )}
            {count > 5 && (
                <span
                    style={{
                        margin: 0,
                        ...getTypographyRoleStyle('captionText', {
                            fontWeight: 300,
                        }),
                        fontSize,
                        color: textColor,
                    }}
                >
                    {formatReviewCount(count, showRating)}
                </span>
            )}
        </div>
    )
}

interface ReviewsProps {
    lpLink: ApiFullLandingPageLink
    location: 'content-block' | 'pdp'
    /** For virtual products: underlying product id for load-more reviews */
    variantShopifyProductId?: string | null
}

const ReviewsContainer = styled.div({
    width: '100%',
    ...inScrollingLayout({
        maxWidth: ScrollingLayout.PROSE_MAX_WIDTH,
        marginInline: 'auto',
    }),
})

/**
 * State (reviews/pagination/expansion) is only initialized from props on
 * mount; callers must pass a `key` derived from `lpLink.linkHash`/
 * `variantShopifyProductId` to reset it on product or variant change (see
 * usage in ReviewsSection.tsx).
 */
export const Reviews = React.forwardRef<HTMLDivElement, ReviewsProps>(
    ({ lpLink, location, variantShopifyProductId }, ref) => {
        const { Analytics } = useContext(AnalyticsContext)
        const initialReviewContent =
            getDisplayData(lpLink, variantShopifyProductId ?? null)
                .reviewContent ?? []
        const [reviews, setReviews] =
            useState<ApiReview[]>(initialReviewContent)
        const [pageNum, setPageNum] = useState<number>(0)
        const [hasMorePagesToLoad, setHasMorePagesToLoad] = useState<boolean>(
            initialReviewContent.length >= 5,
        )
        const [hasUserExpandedReviews, setHasUserExpandedReviews] =
            useState<boolean>(false)

        const lpLinkRef = useRef(lpLink)
        lpLinkRef.current = lpLink

        useEffect(() => {
            const link = lpLinkRef.current
            const skipFetch =
                pageNum === 0 &&
                (getDisplayData(link, variantShopifyProductId ?? null)
                    .reviewContent?.length ?? 0) > 0
            if (skipFetch) return

            let isCancelled = false
            getReviews(link, pageNum, variantShopifyProductId ?? undefined)
                .then((r) => {
                    if (isCancelled) return

                    if (r.isError) {
                        throw new Error(r.error)
                    }
                    const pageSize = 5
                    if (r.result.reviews.length < pageSize) {
                        setHasMorePagesToLoad(false)
                    } else {
                        setHasMorePagesToLoad(true)
                    }
                    setReviews((prevReviews) =>
                        pageNum === 0
                            ? r.result.reviews
                            : [...prevReviews, ...r.result.reviews],
                    )
                })
                .catch((e) => {
                    if (!isCancelled) {
                        console.error('Error fetching reviews:', e)
                    }
                })

            return () => {
                isCancelled = true
            }
        }, [lpLink.linkHash, pageNum, variantShopifyProductId])

        const aiAnalysis = useMemo(
            () =>
                getDisplayData(lpLink, variantShopifyProductId ?? null)
                    .reviewAnalysis ?? null,
            [lpLink, variantShopifyProductId],
        )

        const handleExpandReviews = () => {
            Analytics.TRACK_SEE_MORE_REVIEWS_CLICK(location)
            setHasUserExpandedReviews(true)
        }

        const handleLoadMorePages = () => {
            Analytics.TRACK_SEE_MORE_REVIEWS_CLICK(location)
            setPageNum(pageNum + 1)
        }

        if (reviews.length === 0) return null

        const visibleReviews = hasUserExpandedReviews
            ? reviews
            : reviews.slice(0, INITIAL_VISIBLE_REVIEW_COUNT)

        return (
            <ReviewsContainer ref={ref}>
                <div>
                    <ReviewsSummary
                        lpLink={lpLink}
                        showHeading
                        interactive={false}
                    />
                </div>
                <div>
                    {aiAnalysis && (
                        <AiCustomerReviewSummary
                            key={`${lpLink.linkHash}-${variantShopifyProductId ?? ''}`}
                            analysis={aiAnalysis}
                            linkHash={lpLink.linkHash}
                        />
                    )}
                    <CustomerPhotos
                        key={`${lpLink.linkHash}-${variantShopifyProductId ?? ''}`}
                        linkHash={lpLink.linkHash}
                        initialMedia={lpLink.reviewMedia}
                        shopifyProductId={variantShopifyProductId}
                        bleed={false}
                    />
                    <div>
                        {visibleReviews.map((r, i) => {
                            const isOverlayCard =
                                !hasUserExpandedReviews &&
                                i === INITIAL_VISIBLE_REVIEW_COUNT - 1 &&
                                reviews.length > INITIAL_VISIBLE_REVIEW_COUNT

                            if (!isOverlayCard) {
                                return <ReviewCard key={i} review={r} />
                            }

                            return (
                                <div
                                    key={i}
                                    style={{
                                        position: 'relative',
                                        cursor: 'pointer',
                                        overflow: 'hidden',
                                    }}
                                    onClick={handleExpandReviews}
                                >
                                    <div
                                        style={{
                                            opacity: 0.65,
                                        }}
                                    >
                                        <ReviewCard review={r} />
                                    </div>
                                    <div
                                        style={{
                                            position: 'absolute',
                                            left: 0,
                                            right: 0,
                                            top: '10%',
                                            bottom: 0,
                                            background: `linear-gradient(
                                        to bottom,
                                        transparent 0%,
                                        color-mix(in srgb, var(${PRIMARY_PAGE_FILL_COLOR_VAR}) 80%, transparent 20%) 30%,
                                        var(${PRIMARY_PAGE_FILL_COLOR_VAR}) 90%
                                    )`,
                                        }}
                                    />
                                    <div
                                        style={{
                                            position: 'absolute',
                                            inset: 0,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            pointerEvents: 'none',
                                        }}
                                    >
                                        <div
                                            style={{
                                                padding: '8px 14px',
                                                borderRadius: 999,
                                                border: `1px solid var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`,
                                                backgroundColor: 'transparent',
                                                color: `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`,
                                                ...getTypographyRoleStyle(
                                                    'buttonText',
                                                    { fontWeight: 500 },
                                                ),
                                                fontSize: scaledFontSize(13),
                                            }}
                                        >
                                            View more reviews
                                        </div>
                                    </div>
                                </div>
                            )
                        })}
                        {hasUserExpandedReviews && hasMorePagesToLoad && (
                            <div
                                style={{
                                    width: '100%',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    marginTop: 16,
                                }}
                            >
                                <SeeMoreReviewsButton
                                    onClick={handleLoadMorePages}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </ReviewsContainer>
        )
    },
)
Reviews.displayName = 'Reviews'
